import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable, of, shareReplay } from 'rxjs';
import { PendingPoliciesResponse, PendingPolicy } from '@ee/common/models';

/**
 * Terms of Service / Privacy Policy acceptance state.
 *
 * There is deliberately no method here that fetches policy CONTENT. The app never renders policy text —
 * `canonical_url` on each pending document points at the marketing site, which is the source of truth.
 */
@Injectable({ providedIn: 'root' })
export class PolicyService {
  private cache$: Observable<PendingPolicy[]> | null = null;

  constructor(private http: HttpClient, @Inject('environment') private environment) {
  }

  /**
   * What the user still has to accept. Cached for the session because the route guard evaluates on every
   * child activation and a single portal navigation activates several.
   *
   * FAILS OPEN on error, and does not cache the failure. A transient 5xx must not lock every user out of
   * the entire product; because the error isn't cached, the next navigation or reload retries, so a blip
   * delays the prompt by one navigation rather than skipping it. The server stays authoritative on the
   * write path (409 on a stale key) and, when enabled, on the API itself (412).
   *
   * The tradeoff is explicit: a sustained /pending outage means an ungated window. Failing closed would
   * turn the same outage into a total product outage.
   */
  pending$(): Observable<PendingPolicy[]> {
    if (!this.cache$) {
      this.cache$ = this.http
        .get<PendingPoliciesResponse>(`${this.environment.api_prefix}api/policies/pending`)
        .pipe(
          map(res => res?.pending ?? []),
          shareReplay({ bufferSize: 1, refCount: false }),
          catchError(() => {
            this.cache$ = null;
            return of<PendingPolicy[]>([]);
          })
        );
    }
    return this.cache$;
  }

  /**
   * Drop the in-memory pending cache without fetching.
   *
   * Logout does not reload the SPA, so without this a second user in the same tab would inherit the
   * previous user's cached pending list (or empty list) and either bypass or get stuck on the gate.
   * Call from auth logout; the next `pending$()` after login refetches for the new session.
   */
  clearCache(): void {
    this.cache$ = null;
  }

  /** Force a refetch. Used after a 409, i.e. a new version was registered between our GET and our POST. */
  refresh(): Observable<PendingPolicy[]> {
    this.clearCache();
    return this.pending$();
  }

  /**
   * Submit EXACTLY the `version_key`s `/pending` handed us — never keys the client synthesised. The server
   * rejects anything unknown or superseded with 409 and derives the record itself. 204 on success.
   */
  accept(versionKeys: string[]): Observable<void> {
    return this.http.post<void>(
      `${this.environment.api_prefix}api/policies/accept`,
      { version_keys: versionKeys }
    );
  }

  /**
   * Seed the cache empty after a successful 204, so the guarded navigation that immediately follows passes
   * without a round trip. Refetching instead would let a read-after-write lag bounce the user straight back
   * to the acceptance page they just cleared.
   */
  markAccepted(): void {
    this.cache$ = of<PendingPolicy[]>([]);
  }
}
